/** Tab-scoped handoff used while the browser is away at an SSO provider. */
export const SSO_RETURN_STORAGE_KEY = 'lms_sso_return_destination';

/**
 * Accept only a path on this origin. URL parsing rejects protocol-relative and
 * backslash-normalized hostnames that a string prefix check would miss.
 */
export function internalPath(candidate: string | null | undefined): string | null {
  if (!candidate || !candidate.startsWith('/')) {
    return null;
  }
  try {
    const base = 'https://lms.invalid';
    const parsed = new URL(candidate, base);
    if (parsed.origin !== base) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/**
 * Destinations that authentication is allowed to resume. Keep this narrower
 * than all same-origin paths: callback and recovery URLs can contain one-time
 * credentials that must never be nested into the login URL.
 */
export function authContinuation(candidate: string | null | undefined): string | null {
  const safePath = internalPath(candidate);
  if (!safePath) return null;

  const parsed = new URL(safePath, 'https://lms.invalid');
  if (parsed.pathname === '/oauth/consent') {
    return '/oauth/consent';
  }
  if (parsed.pathname !== '/dashboard' && !parsed.pathname.startsWith('/dashboard/')) {
    return null;
  }
  const forbiddenParameters = ['code', 'state', 'token', 'continuation_token', 'request'];
  if (forbiddenParameters.some((parameter) => parsed.searchParams.has(parameter))) {
    return null;
  }
  return `${parsed.pathname}${parsed.search}`;
}

export function loginPath(destination: string | null | undefined): string {
  const safeDestination = authContinuation(destination);
  return safeDestination
    ? `/login?next=${encodeURIComponent(safeDestination)}`
    : '/login';
}
